<template>
  <span class="geni-inline" :class="[sizeClass]">
    <span
      aria-hidden="true"
      class="cg-electric-wrap"
      :class="{
        'cg-electric-active': useFullElectricFx,
        'cg-electric-idle': !useFullElectricFx,
      }"
      :style="electricWrapStyle"
    >
      <span class="cg-electric-halo"></span>
      <span class="cg-electric-spark cg-electric-spark-a"></span>
      <span class="cg-electric-spark cg-electric-spark-b"></span>
      <span class="cg-electric-spark cg-electric-spark-c"></span>
      <span class="cg-electric-spark cg-electric-spark-d"></span>
      <Icon name="lightning" :size="iconSize" class="cg-electric-bolt" />
    </span>
    <span class="font-bold" :class="textClass">{{ amount }}</span>
    <span v-if="showLabel" class="cursor-help font-bold" :class="textClass">geni</span>
  </span>
</template>

<script setup lang="ts">
import { computed } from "vue";
import Icon from "@/components/ui/Icon.vue";

const props = withDefaults(
  defineProps<{
    amount: number | string;
    size?: "xs" | "sm" | "md" | "lg" | "xl";
    theme?: "light" | "dark" | "inherit";
    animateIcon?: boolean;
    /** Подпись «geni» рядом с суммой (для стоимости действий обычно выключают). */
    showLabel?: boolean;
  }>(),
  {
    size: "sm",
    theme: "inherit",
    animateIcon: true,
    showLabel: true,
  }
);

const sizeClass = computed(() => {
  if (props.size === "xs") return "text-xs";
  if (props.size === "md") return "text-base";
  if (props.size === "xl") return "text-3xl";
  if (props.size === "lg") return "text-2xl";
  return "text-sm";
});

const iconSize = computed(() => {
  if (props.size === "xs") return 12;
  if (props.size === "md") return 16;
  if (props.size === "lg") return 20;
  if (props.size === "xl") return 24;
  return 14;
});

const textClass = computed(() => {
  if (props.theme === "dark") return "text-white";
  if (props.theme === "inherit") return "text-current";
  return "text-[var(--color-base-black)]";
});

const useFullElectricFx = computed(() => props.animateIcon && (props.size === "md" || props.size === "lg" || props.size === "xl"));

const electricWrapStyle = computed(() => ({
  width: `${iconSize.value + 8}px`,
  height: `${iconSize.value + 8}px`,
}));
</script>

<style scoped lang="scss">
.geni-inline {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.cg-electric-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: -2px;
  margin-right: -1px;
}

.cg-electric-bolt {
  position: relative;
  z-index: 2;
  // animation: cg-bolt-flicker 1.15s ease-in-out infinite;
  color: var(--cg-electric-color, var(--color-brand-primary));
  // filter: drop-shadow(0 0 8px color-mix(in oklab, var(--cg-electric-color, var(--color-brand-primary)) 65%, white));
  // overflow: visible;
}

.cg-electric-bolt::after {
  content: "";
  position: absolute;
  inset: -2px;
  border-radius: 2px;
  background: linear-gradient(
    110deg,
    transparent 0%,
    transparent 36%,
    color-mix(in oklab, white 86%, var(--color-brand-primary) 14%) 50%,
    transparent 64%,
    transparent 100%
  );
  mix-blend-mode: screen;
  opacity: 0;
  transform: translateX(-135%) skewX(-12deg);
  animation: none;
  pointer-events: none;
}

.cg-electric-halo {
  position: absolute;
  inset: 1px;
  border-radius: 999px;
  background: radial-gradient(circle, color-mix(in oklab, var(--cg-electric-color, var(--color-brand-primary)) 42%, white) 0%, transparent 68%);
  animation: cg-halo-pulse 1.3s ease-in-out infinite;
  transform: translateY(1px);
}

.cg-electric-spark {
  position: absolute;
  width: 8px;
  height: 11px;
  background: var(--cg-spark-color, #ffcc00);
  clip-path: polygon(38% 0%, 82% 0%, 57% 35%, 100% 35%, 30% 100%, 46% 58%, 8% 58%);
  box-shadow: 0 0 10px var(--cg-spark-color, #ffcc00);
  opacity: 0;
}

.cg-electric-spark-a {
  --cg-spark-color: #ffcc00;
  top: 1px;
  right: -1px;
  transform-origin: 40% 55%;
  animation: cg-spark-a 1.05s steps(5, end) infinite;
}

.cg-electric-spark-b {
  --cg-spark-color: #ff453a;
  bottom: 1px;
  left: -2px;
  transform-origin: 52% 48%;
  animation: cg-spark-b 1.05s steps(5, end) infinite;
}

.cg-electric-spark-c {
  --cg-spark-color: #ff453a;
  top: -1px;
  left: 3px;
  transform-origin: 50% 50%;
  animation: cg-spark-c 1.05s steps(5, end) infinite;
}

.cg-electric-spark-d {
  --cg-spark-color: #ffcc00;
  bottom: -1px;
  right: 2px;
  transform-origin: 50% 50%;
  animation: cg-spark-d 1.05s steps(5, end) infinite;
}

.cg-electric-idle .cg-electric-bolt,
.cg-electric-idle .cg-electric-halo,
.cg-electric-idle .cg-electric-spark,
.cg-electric-idle .cg-electric-bolt::after {
  animation-play-state: paused !important;
}

.cg-electric-idle .cg-electric-halo,
.cg-electric-idle .cg-electric-spark {
  opacity: 0;
}

.cg-electric-idle .cg-electric-halo {
  opacity: 0.2;
  transform: translateY(1px) scale(0.9);
}

.geni-inline--light {
  color: #ffffff;
}

.geni-inline--dark {
  color: var(--popover-foreground);
}

.geni-inline--inherit {
  color: inherit;
}

.text-xs {
  font-size: 12px;
}

.text-sm {
  font-size: 14px;
}

.text-base {
  font-size: 16px;
}

.text-2xl {
  font-size: 20px;
}

.text-3xl {
  font-size: 24px;
}

.font-bold {
  font-weight: 700;
}

.cursor-help {
  cursor: help;
}

.text-white {
  color: #ffffff;
}

.text-current {
  color: currentColor;
}

@keyframes cg-bolt-flicker {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.94;
    filter: drop-shadow(0 0 6px color-mix(in oklab, var(--color-brand-primary) 58%, white));
  }
  18% {
    transform: scale(1.05);
    opacity: 1;
    filter: drop-shadow(0 0 10px color-mix(in oklab, var(--color-brand-primary) 76%, white));
  }
  32% {
    transform: scale(0.97);
    opacity: 0.9;
  }
  48% {
    transform: scale(1.03);
    opacity: 0.98;
  }
  68% {
    transform: scale(0.99);
    opacity: 0.94;
  }
  84% {
    transform: scale(1.02);
    opacity: 0.98;
  }
}

@keyframes cg-bolt-sweep {
  0%,
  24%,
  100% {
    opacity: 0;
    transform: translateX(-135%) skewX(-12deg);
  }
  30% {
    opacity: 0.9;
    transform: translateX(-35%) skewX(-12deg);
  }
  40% {
    opacity: 0.75;
    transform: translateX(28%) skewX(-12deg);
  }
  50% {
    opacity: 0;
    transform: translateX(120%) skewX(-12deg);
  }
}

@keyframes cg-halo-pulse {
  0%,
  100% {
    opacity: 0.35;
    transform: scale(0.86);
  }
  40% {
    opacity: 0.7;
    transform: scale(1.08);
  }
  65% {
    opacity: 0.45;
    transform: scale(0.95);
  }
}

@keyframes cg-spark-a {
  0%,
  100% {
    opacity: 0;
    transform: translate(0, 0) rotate(8deg) scale(0.6);
  }
  8% {
    opacity: 1;
    transform: translate(1px, -1px) rotate(16deg) scale(1);
  }
  14% {
    opacity: 0;
    transform: translate(4px, -3px) rotate(22deg) scale(0.8);
  }
  24% {
    opacity: 0.95;
    transform: translate(2px, -2px) rotate(14deg) scale(0.96);
  }
  31% {
    opacity: 0;
    transform: translate(6px, -4px) rotate(23deg) scale(0.7);
  }
}

@keyframes cg-spark-b {
  0%,
  100% {
    opacity: 0;
    transform: translate(0, 0) rotate(-12deg) scale(0.6);
  }
  42% {
    opacity: 0;
    transform: translate(0, 0) rotate(-12deg) scale(0.6);
  }
  52% {
    opacity: 1;
    transform: translate(-1px, 1px) rotate(-22deg) scale(1);
  }
  58% {
    opacity: 0;
    transform: translate(-4px, 3px) rotate(-26deg) scale(0.8);
  }
  68% {
    opacity: 0.9;
    transform: translate(-2px, 2px) rotate(-20deg) scale(0.94);
  }
  75% {
    opacity: 0;
    transform: translate(-6px, 5px) rotate(-28deg) scale(0.72);
  }
}

@keyframes cg-spark-c {
  0%,
  100% {
    opacity: 0;
    transform: translate(0, 0) rotate(2deg) scale(0.58);
  }
  30% {
    opacity: 0;
    transform: translate(0, 0) rotate(2deg) scale(0.58);
  }
  36% {
    opacity: 0.95;
    transform: translate(1px, -2px) rotate(10deg) scale(0.98);
  }
  43% {
    opacity: 0;
    transform: translate(3px, -5px) rotate(16deg) scale(0.72);
  }
}

@keyframes cg-spark-d {
  0%,
  100% {
    opacity: 0;
    transform: translate(0, 0) rotate(-4deg) scale(0.58);
  }
  60% {
    opacity: 0;
    transform: translate(0, 0) rotate(-4deg) scale(0.58);
  }
  67% {
    opacity: 0.95;
    transform: translate(1px, 2px) rotate(-11deg) scale(0.98);
  }
  74% {
    opacity: 0;
    transform: translate(3px, 5px) rotate(-18deg) scale(0.72);
  }
}

@media (prefers-reduced-motion: reduce) {
  .cg-electric-bolt,
  .cg-electric-halo,
  .cg-electric-spark,
  .cg-electric-bolt::after {
    animation: none !important;
  }
}
</style>
